import { useEffect, useRef, useState } from "react";
import api, { errMsg, fmtMinutes } from "@/lib/api";
import { toast } from "sonner";
import { Play, Square, Timer } from "lucide-react";

function Elapsed({ startedAt }) {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    const start = new Date(startedAt).getTime();
    const tick = () => setSecs(Math.max(0, Math.floor((Date.now() - start) / 1000)));
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [startedAt]);
  const h = String(Math.floor(secs / 3600)).padStart(2, "0");
  const m = String(Math.floor((secs % 3600) / 60)).padStart(2, "0");
  const s = String(secs % 60).padStart(2, "0");
  return (
    <span data-testid="active-timer-digits" className="font-mono-digits text-3xl sm:text-4xl font-bold tracking-widest tabular-nums text-emerald-400 animate-pulse">
      {h}:{m}:{s}
    </span>
  );
}

export default function TimeTracker() {
  const [timer, setTimer] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [entries, setEntries] = useState([]);
  const [pickTask, setPickTask] = useState("");
  const [manual, setManual] = useState({ label: "", minutes: "", note: "" });
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const [tm, t, e] = await Promise.all([
        api.get("/timer/active"),
        api.get("/tasks-with-time"),
        api.get("/time-entries"),
      ]);
      setTimer(tm.data.timer);
      setTasks(t.data);
      setEntries(e.data);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get("/timer/active");
        setTimer(r.data.timer);
      } catch { /* ignore */ }
    }, 10000);
    return () => clearInterval(pollRef.current);
  }, []);

  const start = async () => {
    if (!pickTask) return toast.error("Pick a task first");
    try {
      await api.post("/timer/start", { task_id: pickTask });
      toast.success("Timer started");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const stop = async () => {
    try {
      const r = await api.post("/timer/stop");
      toast.success(`Logged ${fmtMinutes(r.data.minutes)} — R${r.data.amount.toFixed(2)}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const logManual = async (e) => {
    e.preventDefault();
    try {
      await api.post("/time-entries", { label: manual.label, minutes: parseInt(manual.minutes, 10), note: manual.note });
      toast.success("Time entry saved");
      setManual({ label: "", minutes: "", note: "" });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="space-y-8" data-testid="timekeeper-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Timekeeper</h1>
        <p className="text-sm text-muted-foreground mt-1">Run a live timer on a task or log time manually. Rates apply automatically.</p>
      </div>

      <div className="rounded-lg border border-border bg-card p-6">
        {timer ? (
          <div className="flex items-center justify-between gap-6 flex-wrap">
            <div>
              <p className="font-mono-digits text-[10px] uppercase tracking-widest text-muted-foreground">Running on</p>
              <p className="text-lg font-semibold mt-1">{timer.task_title}</p>
              <p className="font-mono-digits text-xs text-orange-400 mt-0.5">{timer.ticket_number}</p>
            </div>
            <Elapsed startedAt={timer.started_at} />
            <button data-testid="stop-timer-button" onClick={stop}
              className="flex items-center gap-2 rounded-md bg-red-500/90 hover:bg-red-500 active:scale-[0.98] text-white text-sm font-semibold px-5 py-2.5 transition-all">
              <Square className="h-4 w-4" /> Stop & log
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-4 flex-wrap">
            <Timer className="h-5 w-5 text-muted-foreground" />
            <select value={pickTask} onChange={(e) => setPickTask(e.target.value)} data-testid="timer-task-select"
              className="flex-1 min-w-[220px] rounded-md bg-secondary/60 border border-border px-3 py-2.5 text-sm outline-none focus:border-orange-500/60">
              <option value="">Select a task to time…</option>
              {tasks.filter((t) => t.status !== "done").map((t) => (
                <option key={t.id} value={t.id}>{t.ticket_number} — {t.title}</option>
              ))}
            </select>
            <button data-testid="start-timer-button" onClick={start}
              className="flex items-center gap-2 rounded-md bg-emerald-500 hover:bg-emerald-600 active:scale-[0.98] text-white text-sm font-semibold px-5 py-2.5 transition-all">
              <Play className="h-4 w-4" /> Start timer
            </button>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-border bg-card p-6">
        <h2 className="text-lg font-semibold mb-4">Manual entry</h2>
        <form data-testid="manual-time-form" onSubmit={logManual} className="grid sm:grid-cols-4 gap-4">
          <input required placeholder="What did you work on?" value={manual.label}
            onChange={(e) => setManual({ ...manual, label: e.target.value })}
            data-testid="manual-label-input"
            className="rounded-md bg-secondary/60 border border-border px-3 py-2 text-sm outline-none focus:border-orange-500/60" />
          <input required type="number" min="1" placeholder="Minutes" value={manual.minutes}
            onChange={(e) => setManual({ ...manual, minutes: e.target.value })}
            data-testid="manual-minutes-input"
            className="rounded-md bg-secondary/60 border border-border px-3 py-2 text-sm outline-none focus:border-orange-500/60" />
          <input placeholder="Note (optional)" value={manual.note}
            onChange={(e) => setManual({ ...manual, note: e.target.value })}
            data-testid="manual-note-input"
            className="rounded-md bg-secondary/60 border border-border px-3 py-2 text-sm outline-none focus:border-orange-500/60" />
          <button type="submit" data-testid="manual-log-button"
            className="rounded-md bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold px-4 py-2 transition-colors">
            Log time
          </button>
        </form>
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-lg font-semibold">Recent logs</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left font-mono-digits text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
                <th className="px-6 py-3">When</th>
                <th className="px-6 py-3">Task</th>
                <th className="px-6 py-3">Agent</th>
                <th className="px-6 py-3">Time</th>
                <th className="px-6 py-3">Rate</th>
                <th className="px-6 py-3">Amount</th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 && (
                <tr><td colSpan={6} className="px-6 py-8 text-center text-muted-foreground text-sm">No time logged yet.</td></tr>
              )}
              {entries.slice(0, 30).map((e) => (
                <tr key={e.id} data-testid={`time-entry-${e.id}`} className="border-b border-border/50 hover:bg-secondary/30 transition-colors">
                  <td className="px-6 py-3 text-muted-foreground whitespace-nowrap">{new Date(e.created_at).toLocaleString()}</td>
                  <td className="px-6 py-3">
                    <span className="font-mono-digits text-xs text-orange-400 mr-2">{e.ticket_number}</span>
                    {e.task_title}
                    {e.source === "timer" && <span className="ml-2 text-[10px] font-mono-digits uppercase text-emerald-400">timer</span>}
                  </td>
                  <td className="px-6 py-3">{e.user_name}</td>
                  <td className="px-6 py-3 font-mono-digits">{fmtMinutes(e.minutes)}</td>
                  <td className="px-6 py-3 font-mono-digits text-muted-foreground">R{e.hourly_rate}/h</td>
                  <td className="px-6 py-3 font-mono-digits text-emerald-400">R{e.amount.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
